import { useState, useCallback, useEffect, useRef } from "react";

import { createPortal } from "react-dom";
import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes, setElevation } from "@similarweb/styles";
import { Autocomplete, DotsLoader, ItemIconChain, ListItemWebsite, Tooltip } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { autocompleteStates } from "app/components/compare/WebsiteQueryBarTypes";
import { translate } from "app/filters/i18nFilter";
import { WebsitePageSearchTypeNames } from "app/pages/digital-marketing/keywords-campaigns/wizard/constants";
import { getWebsiteResults } from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";
import { BLOCK_SITE_STATUS } from "pro-features/components/AutocompleteDigitalMarketing/AutocompleteDigitalMarketingHelper";
import type { IWebsiteItem } from "pro-features/components/AutocompleteWebsites/types";

const StyledAutocomplete = styled(Autocomplete)<{
    height: string;
    $hasWebsiteIcon?: boolean;
    $hideDropdown?: boolean;
}>`
    height: ${({ height }) => height};
    z-index: 2;

    .AutoCompleteContainer {
        .input-container {
            height: ${({ height }) => height};

            & input {
                ${({ $hasWebsiteIcon }) =>
                    $hasWebsiteIcon &&
                    `
                    padding-left: 34px;
                `}
            }
        }

        ${({ $hideDropdown }) =>
            $hideDropdown &&
            `
            .ListItemsContainer {
                display: none !important;
            }
        `}
    }
`;

const PortalDropdownContainer = styled.div<{ top: number; left: number; width: number }>`
    position: fixed;
    top: ${({ top }) => top}px;
    left: ${({ left }) => left}px;
    width: ${({ width }) => width}px;
    background: ${colorsPalettes.carbon[0]};
    border: 1px solid ${colorsPalettes.carbon[50]};
    border-radius: 3px;
    ${setElevation("Z2")};
    max-height: min(300px, calc(100vh - ${({ top }) => top}px - 20px));
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 10000;
`;

export const INPUT_HEIGHT_PX = 38;
export const INPUT_HEIGHT = `${INPUT_HEIGHT_PX}px`;

export const InlineErrorMessage = styled(Text)`
    left: 0;
    right: 0;
    line-height: 18px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

interface LLMDomainAutocompleteProps {
    selectedDomain: IWebsiteItem | null;
    setSelectedDomain: (domain: IWebsiteItem | null) => void;
    recentItems?: Array<{ data: { mainItem: string; resources: { mainItemFavIcon: string } } }>;
    isFetching?: boolean;
    placeholder?: string;
    width?: string;
    height?: string;
    isDisabled?: boolean;
    tooltip?: string;
    searchIcon?: IconName;
    onCloseClick?: () => void;
    displaySelectedDomainIcon?: boolean;
    usePortalForDropdown?: boolean;
    autoFocus?: boolean;
    error?: boolean;
}

export const LLMDomainAutocomplete = ({
    selectedDomain,
    setSelectedDomain,
    recentItems = [],
    isFetching = false,
    placeholder,
    width = "630px",
    height = "48px",
    isDisabled = false,
    tooltip = "",
    searchIcon = null,
    onCloseClick,
    displaySelectedDomainIcon = false,
    usePortalForDropdown = false,
    autoFocus = false,
    error = false,
}: LLMDomainAutocompleteProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const autocompleteRef = useRef(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const shouldDisplayWebsiteIcon = displaySelectedDomainIcon && !!selectedDomain?.image;

    useEffect(() => {
        if (!selectedDomain && !!autocompleteRef.current?.textFieldRef) {
            autocompleteRef.current.textFieldRef.clearValue();
        }
    }, [selectedDomain]);

    useEffect(() => {
        if (!usePortalForDropdown || !containerRef.current) {
            return;
        }

        const updatePosition = () => {
            const rect = containerRef.current?.getBoundingClientRect();
            if (rect) {
                setDropdownPosition({
                    top: rect.bottom,
                    left: rect.left,
                    width: rect.width,
                });
            }
        };

        updatePosition();
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);

        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [usePortalForDropdown]);

    const recentSearches = recentItems.map((item) => {
        if (!item.data?.mainItem) {
            return autocompleteStates.NO_DATA_RECENTS;
        }
        const websiteItem: IWebsiteItem = {
            name: item.data.mainItem,
            image: item.data.resources.mainItemFavIcon || "",
            searchType: WebsitePageSearchTypeNames.MainDomain,
            displayText: item.data.mainItem,
            url: item.data.mainItem,
        };
        return (
            <ListItemWebsite
                key={websiteItem.name}
                img={websiteItem.image}
                text={websiteItem.name}
                onClick={() => handleDomainSelect(websiteItem)}
                data-automation="llm-homepage-recent-item"
            />
        );
    });

    const getDomainsData = useCallback(
        async (query: string) => {
            if (!query?.length) {
                return recentSearches;
            }

            query = query?.trim();

            setIsLoading(true);
            try {
                const queries = await getWebsiteResults(query);
                if (queries.length === 0) {
                    return [autocompleteStates.NO_DATA_SEARCHES];
                }

                const filteredQueries = queries.filter((res) =>
                    Array.isArray(res.blockStatus) ? !res.blockStatus.includes(BLOCK_SITE_STATUS) : true,
                );

                return filteredQueries.map((site) => {
                    const websiteItem: IWebsiteItem = {
                        name: site.name,
                        image: site.image || "",
                        searchType: WebsitePageSearchTypeNames.MainDomain,
                        displayText: site.name,
                        url: site.name,
                    };
                    return (
                        <ListItemWebsite
                            key={websiteItem.name}
                            img={websiteItem.image}
                            text={websiteItem.name}
                            onClick={() => handleDomainSelect(websiteItem)}
                            data-automation="llm-homepage-search-result-item"
                        />
                    );
                });
            } catch (err) {
                Sentry.error("Error fetching domains - LLM Domain Autocomplete", err as Error);
                return [autocompleteStates.ERROR];
            } finally {
                setIsLoading(false);
            }
        },
        [recentSearches],
    );

    const handleDomainSelect = (site: IWebsiteItem) => {
        setSelectedDomain(site);
    };

    const renderItems = useCallback(
        ({ listItems }) => {
            if (!listItems?.length) {
                return null;
            }

            const dropdownContent = (
                <div data-automation="llm-homepage-search-results-container">
                    {listItems.map((item) => {
                        if (item === autocompleteStates.NO_DATA_SEARCHES) {
                            return (
                                <Box
                                    key="no-data-searches"
                                    data-automation="llm-homepage-no-search-results"
                                    padding="0 0 0 16px"
                                >
                                    {translate("autocomplete.websitesCompare.searches.nodata")}
                                </Box>
                            );
                        }
                        if (item === autocompleteStates.NO_DATA_RECENTS) {
                            return (
                                <div
                                    key="no-data-recents"
                                    data-automation="llm-homepage-no-recent-results"
                                >
                                    {translate("autocomplete.websitesMain.recents.nodata")}
                                </div>
                            );
                        }
                        if (item === autocompleteStates.ERROR) {
                            return (
                                <div
                                    key="error"
                                    data-automation="llm-homepage-error-state"
                                >
                                    {translate("autocomplete.error")}
                                </div>
                            );
                        }
                        return item;
                    })}
                </div>
            );

            if (usePortalForDropdown) {
                return (
                    <>
                        {createPortal(
                            <PortalDropdownContainer
                                data-react-aria-top-layer="true"
                                top={dropdownPosition.top}
                                left={dropdownPosition.left}
                                width={dropdownPosition.width}
                            >
                                {dropdownContent}
                            </PortalDropdownContainer>,
                            document.body,
                        )}
                    </>
                );
            } else {
                return dropdownContent;
            }
        },
        [usePortalForDropdown, dropdownPosition],
    );

    return (
        <Flex
            alignItems="center"
            gap="8px"
            width={width}
            height={height}
            data-automation="llm-homepage-search-box"
        >
            <Box
                flex="1"
                height={height}
                ref={containerRef}
            >
                {!isFetching ? (
                    <Tooltip
                        enabled={!!tooltip}
                        content={tooltip}
                    >
                        <Box position="relative">
                            {shouldDisplayWebsiteIcon ? (
                                <Flex
                                    alignItems="center"
                                    position="absolute"
                                    left="16px"
                                    height="100%"
                                    padding="1px 0 0"
                                    zIndex={5}
                                >
                                    <ItemIconChain
                                        size={24}
                                        icons={[selectedDomain.image]}
                                    />
                                </Flex>
                            ) : null}
                            <StyledAutocomplete
                                floating={true}
                                disabled={isDisabled}
                                debounce={250}
                                isLoading={isLoading}
                                height={height}
                                loadingComponent={<DotsLoader data-automation="llm-homepage-loading-indicator" />}
                                placeholder={placeholder}
                                getListItems={getDomainsData}
                                renderItems={renderItems}
                                maxResults={10}
                                searchIcon={searchIcon}
                                data-automation="llm-homepage-search-input"
                                onFocus={isDisabled ? undefined : () => setSelectedDomain(null)}
                                selectedValue={
                                    selectedDomain?.displayText ?? selectedDomain?.name ?? selectedDomain?.url
                                }
                                onCloseClick={onCloseClick}
                                ref={autocompleteRef}
                                $hasWebsiteIcon={shouldDisplayWebsiteIcon}
                                $hideDropdown={usePortalForDropdown}
                                isError={error}
                                errorIndicator={error}
                                autoFocus={autoFocus}
                            />
                        </Box>
                    </Tooltip>
                ) : null}
            </Box>
        </Flex>
    );
};
